import React, { useEffect } from 'react';

import { tCI, translateCIError } from '../../i18n/customerIdentity';

/**
 * Shared UI primitives for the Customer Identity admin pages.
 *
 *   - <CIDrawer />          right-slide modal (Bootstrap-friendly)
 *   - <CIConfirmPopup />    custom confirmation popup
 *   - <CIInfoBar />         system message bar above the page header
 *   - <CIInlineAlert />     same alert, sized for use inside drawers/forms
 *
 * Message shape (used by both <CIInfoBar /> and <CIInlineAlert />):
 *   { kind: 'success' | 'danger' | 'warning' | 'info',
 *     // Pick one of:
 *     text?:      string,           // pre-translated text (used as-is)
 *     i18nKey?:   string,           // dictionary key (e.g. 'users.saved')
 *     i18nParams?: object,          // params for placeholder substitution
 *     error?:     CIError | string, // a thrown error (translated automatically)
 *   }
 */

const renderMessageText = (message) => {
  if (!message) return null;
  if (message.error) return translateCIError(message.error);
  if (message.i18nKey) return tCI(message.i18nKey, message.i18nParams);
  return message.text || null;
};

export function CIInfoBar({ message, onDismiss }) {
  const text = renderMessageText(message);
  if (!text) return null;
  const variant = message.kind || 'info';
  return (
    <div
      className={`alert alert-${variant} d-flex justify-content-between align-items-center py-2 px-3 mb-0 rounded-0`}
      role={variant === 'danger' ? 'alert' : 'status'}
    >
      <div>{text}</div>
      {onDismiss ? (
        <button
          type="button"
          className="btn-close"
          aria-label={tCI('common.close')}
          onClick={onDismiss}
        />
      ) : null}
    </div>
  );
}

export function CIInlineAlert({ message, onDismiss, className = '' }) {
  const text = renderMessageText(message);
  if (!text) return null;
  const variant = message.kind || 'info';
  return (
    <div
      className={`alert alert-${variant} d-flex justify-content-between align-items-center mb-3 ${className}`.trim()}
      role={variant === 'danger' ? 'alert' : 'status'}
    >
      <div className="me-3">{text}</div>
      {onDismiss ? (
        <button
          type="button"
          className="btn-close"
          aria-label={tCI('common.close')}
          onClick={onDismiss}
        />
      ) : null}
    </div>
  );
}

export function CIConfirmPopup({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  danger = false,
}) {
  const resolvedTitle = title || tCI('common.confirm');
  const resolvedConfirm = confirmLabel || tCI('common.confirm');
  const resolvedCancel = cancelLabel || tCI('common.cancel');
  if (!open) return null;
  return (
    <div
      className="modal d-block"
      role="dialog"
      aria-modal="true"
      style={{ background: 'rgba(0,0,0,.45)', zIndex: 1200 }}
      onClick={onCancel}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{resolvedTitle}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onCancel}
              aria-label={tCI('common.close')}
            />
          </div>
          <div className="modal-body">{message}</div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              {resolvedCancel}
            </button>
            <button
              type="button"
              className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
              onClick={onConfirm}
            >
              {resolvedConfirm}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CIDrawer({
  open,
  title,
  onClose,
  children,
  footer,
  defaultWidthPct = 50,
  minWidthPct = 25,
  maxWidthPct = 75,
}) {
  const [widthPct, setWidthPct] = React.useState(defaultWidthPct);
  const startXRef = React.useRef(null);
  const startWidthRef = React.useRef(defaultWidthPct);

  useEffect(() => {
    if (open) setWidthPct(defaultWidthPct);
  }, [open, defaultWidthPct]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const startResize = (e) => {
    startXRef.current = e.clientX;
    startWidthRef.current = widthPct;
    document.addEventListener('mousemove', onResize);
    document.addEventListener('mouseup', stopResize);
  };
  const onResize = (e) => {
    if (startXRef.current == null) return;
    const dx = startXRef.current - e.clientX;
    const vw = window.innerWidth || 1;
    let next = startWidthRef.current + (dx / vw) * 100;
    if (next < minWidthPct) next = minWidthPct;
    if (next > maxWidthPct) next = maxWidthPct;
    setWidthPct(next);
  };
  const stopResize = () => {
    startXRef.current = null;
    document.removeEventListener('mousemove', onResize);
    document.removeEventListener('mouseup', stopResize);
  };

  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
        zIndex: 1080, display: 'flex', justifyContent: 'flex-end',
      }}
      onClick={onClose}
    >
      <div
        className="bg-white d-flex flex-column shadow"
        style={{ width: `${widthPct}%`, height: '100%', position: 'relative' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          onMouseDown={startResize}
          aria-label={tCI('common.resizeDrawer')}
          role="separator"
          style={{ position: 'absolute', left: 0, top: 0, width: 6, height: '100%', cursor: 'col-resize' }}
        />
        <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
          <h5 className="m-0">{title}</h5>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            {tCI('common.close')}
          </button>
        </div>
        <div className="flex-fill p-3" style={{ overflowY: 'auto' }}>{children}</div>
        {footer ? <div className="border-top p-2 d-flex justify-content-end gap-2">{footer}</div> : null}
      </div>
    </div>
  );
}
